import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLoaderData } from 'react-router-dom';

import Location from '@r6digital/rapidstor-types/react/entities/Location';

import { usePostReservationSaveDocumentsMutation } from '@/api/rapidstor';
import { WizardValidationError } from '@/types/Wizard';

import Alert from '@/components/elements/alert';
import ErrorTag from '@/components/elements/error-tag';

import classes from './styles.module.scss';

type UploadState = 'idle' | 'uploading' | 'success' | 'error';

interface Props {
    updateFile: (key: string, file: File | null) => void;
    resToken: string | null | undefined;
    resTenant: number | null | undefined;
    validationErrors: WizardValidationError[];
}

export default function ProofOfInsurance({
    updateFile,
    resToken,
    resTenant,
    validationErrors,
}: Props) {
    const { t } = useTranslation();
    const location = useLoaderData() as Location;
    const [postReservationSaveDocuments] = usePostReservationSaveDocumentsMutation();

    const proofOfInsuranceRef = useRef<HTMLInputElement>(null);

    const [file, setFile] = useState<File | null>(null);
    const [uploadState, setUploadState] = useState<UploadState>('idle');

    const formErrors = useMemo(() => {
        let inputNames: string[] = [];
        inputNames = ['proofOfInsuranceFile'];

        return validationErrors?.filter((error) => inputNames.includes(error.name));
    }, [validationErrors]);

    const getUploadMappedStatus = () => {
        switch (uploadState) {
            case 'uploading':
                return 'info';
            case 'success':
                return 'success';
            case 'error':
                return 'error';
            default:
                return 'info';
        }
    };

    const getUploadMappedText = () => {
        switch (uploadState) {
            case 'uploading':
                return t('wizard:insuranceProof:uploading');
            case 'success':
                return t('wizard:insuranceProof:success');
            case 'error':
                return t('wizard:insuranceProof:error');
            default:
                return file?.name || '';
        }
    };

    const handleUpload = async (file: File | null) => {
        if (!file || !resToken || !resTenant || !window.rapidstor_settings.rapidstor_options.corpToken || !window.rapidstor_settings.rapidstor_options.locationToken) {
            return;
        }

        const formData = new FormData();
        formData.append('proofOfInsurance', file);

        try {
            await postReservationSaveDocuments({
                documents: formData,
                corpToken: window.rapidstor_settings.rapidstor_options.corpToken,
                locationToken: window.rapidstor_settings.rapidstor_options.locationToken,
                reservationToken: resToken,
                tenantId: resTenant,
            });
            setUploadState('success');
        } catch (error) {
            setUploadState('error');
        }
    };

    return (
        <div className={`${classes.container}`}>
            <span className={`${classes.header_text}`}>
                {t('wizard:insuranceProof:title')}
            </span>
            <span className={`${classes.subtitle}`}>
                {t('wizard:insuranceProof:subtitle')}
            </span>

            {formErrors?.map((error) => (
                <ErrorTag key={`${error.name}_error_idx`} error={error} />
            ))}

            <div
                className={`${classes.upload} ${(uploadState === 'uploading' || uploadState === 'success') &&
                    classes.disabled
                    }`}
            >
                <input
                    type='file'
                    ref={proofOfInsuranceRef}
                    onChange={(e) => {
                        setFile(e.target.files ? e.target.files[0] : null);
                        handleUpload(e.target.files ? e.target.files[0] : null);
                        setUploadState('uploading');
                        if (e.target.files) {
                            updateFile('proofOfInsuranceFile', e.target.files[0]);
                        } else {
                            updateFile('proofOfInsuranceFile', null);
                        }
                    }}
                    disabled={uploadState === 'uploading' || uploadState === 'success'}
                    accept='image/*,.pdf'
                    required
                    max={1}
                />
                <svg
                    width='19'
                    height='17'
                    viewBox='0 0 19 17'
                    fill='none'
                    xmlns='http://www.w3.org/2000/svg'
                >
                    <path
                        d='M18.5 10.6252V15.5834C18.5 15.9591 18.342 16.3194 18.0607 16.5851C17.7794 16.8507 17.3978 17 17 17H2C1.60218 17 1.22064 16.8507 0.93934 16.5851C0.658035 16.3194 0.5 15.9591 0.5 15.5834V10.6252C0.5 10.4374 0.579018 10.2572 0.71967 10.1244C0.860322 9.99152 1.05109 9.9169 1.25 9.9169C1.44891 9.9169 1.63968 9.99152 1.78033 10.1244C1.92098 10.2572 2 10.4374 2 10.6252V15.5834H17V10.6252C17 10.4374 17.079 10.2572 17.2197 10.1244C17.3603 9.99152 17.5511 9.9169 17.75 9.9169C17.9489 9.9169 18.1397 9.99152 18.2803 10.1244C18.421 10.2572 18.5 10.4374 18.5 10.6252ZM6.28063 4.75155L8.75 2.41855V10.6252C8.75 10.8131 8.82902 10.9932 8.96967 11.1261C9.11032 11.2589 9.30109 11.3335 9.5 11.3335C9.69891 11.3335 9.88968 11.2589 10.0303 11.1261C10.171 10.9932 10.25 10.8131 10.25 10.6252V2.41855L12.7194 4.75155C12.8601 4.88446 13.051 4.95912 13.25 4.95912C13.449 4.95912 13.6399 4.88446 13.7806 4.75155C13.9214 4.61864 14.0004 4.43838 14.0004 4.25042C14.0004 4.06246 13.9214 3.8822 13.7806 3.74929L10.0306 0.207738C9.96097 0.141882 9.87825 0.0896375 9.7872 0.0539923C9.69616 0.0183471 9.59856 0 9.5 0C9.40144 0 9.30384 0.0183471 9.2128 0.0539923C9.12175 0.0896375 9.03903 0.141882 8.96937 0.207738L5.21937 3.74929C5.07864 3.8822 4.99958 4.06246 4.99958 4.25042C4.99958 4.43838 5.07864 4.61864 5.21937 4.75155C5.36011 4.88446 5.55098 4.95912 5.75 4.95912C5.94902 4.95912 6.13989 4.88446 6.28063 4.75155Z'
                        fill='#7B7B7B'
                    />
                </svg>
                <span>
                    {
                        location.rapidstor.tenantAccounts.uploadDocuments.proofOfInsurance
                            .content
                    }
                </span>
            </div>

            {file && (
                <Alert type={getUploadMappedStatus()} text={getUploadMappedText()} />
            )}
        </div>
    );
}
